import React, { useState, useEffect } from 'react';
import { SupplementCatalog } from '../models/diary';
import { CreateSupplementData } from '../services/supplementCatalogService';
import { useIsMobile } from '../hooks/useIsMobile';

interface SupplementFormModalProps {
  open: boolean;
  item?: SupplementCatalog;
  onSave: (data: Partial<CreateSupplementData>) => Promise<void>;
  onCancel: () => void;
}

const SupplementFormModal: React.FC<SupplementFormModalProps> = ({
  open,
  item,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState<CreateSupplementData>({
    name: '',
    purchaseUrl: '',
    description: '',
  });
  const [originalData, setOriginalData] = useState<CreateSupplementData | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateSupplementData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open) {
      if (item) {
        const initial = { name: item.name, purchaseUrl: item.purchaseUrl || '', description: item.description || '' };
        setFormData(initial);
        setOriginalData(initial);
      } else {
        setFormData({ name: '', purchaseUrl: '', description: '' });
        setOriginalData(null);
      }
      setErrors({});
      setSubmitError('');
    }
  }, [open, item]);

  const handleFieldChange = (field: keyof CreateSupplementData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    setSubmitError('');
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateSupplementData, string>> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (formData.purchaseUrl && formData.purchaseUrl.trim()) {
      try {
        new URL(formData.purchaseUrl);
      } catch {
        newErrors.purchaseUrl = 'La URL no es válida';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const dataToSave: Partial<CreateSupplementData> = isEdit && originalData
        ? (Object.fromEntries(
            Object.entries(formData).filter(([k, v]) => v !== originalData[k as keyof CreateSupplementData])
          ) as Partial<CreateSupplementData>)
        : formData;
      await onSave(dataToSave);
    } catch (error: any) {
      if (error.response && typeof error.response === 'object') {
        const backendErrors: Partial<Record<keyof CreateSupplementData, string>> = {};
        let hasFieldErrors = false;
        Object.keys(error.response).forEach((field) => {
          const errorMessages = error.response[field];
          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
            hasFieldErrors = true;
            if (field in formData) {
              backendErrors[field as keyof CreateSupplementData] = errorMessages[0];
            }
          }
        });
        if (hasFieldErrors) {
          setErrors(backendErrors);
          setSubmitError('Por favor corrige los errores en el formulario.');
        } else {
          setSubmitError(error.message || 'Error al guardar. Por favor intente nuevamente.');
        }
      } else {
        setSubmitError(error.message || 'Error al guardar. Por favor intente nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const isEdit = !!item;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.18)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: isMobile ? '12px' : '20px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: isMobile ? '24px 20px' : '32px 40px',
        width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 22, fontWeight: 600, color: '#1f2937' }}>
          {isEdit ? 'Editar Suplemento' : 'Nuevo Suplemento'}
        </h2>

        {submitError && (
          <div style={{
            background: '#fee2e2', color: '#dc2626',
            padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14,
          }}>
            {submitError}
          </div>
        )}

        {/* Nombre */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Nombre <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input
            value={formData.name}
            onChange={e => handleFieldChange('name', e.target.value)}
            style={{
              width: '100%', padding: '11px 14px',
              border: `1px solid ${errors.name ? '#dc2626' : '#cbd5e1'}`,
              borderRadius: 6, fontSize: 15, outline: 'none',
              boxSizing: 'border-box', transition: 'border-color 0.2s',
            }}
            placeholder="Ej: Omega 3"
          />
          {errors.name && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.name}
            </span>
          )}
        </label>

        {/* URL de compra */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            URL de compra
          </span>
          <input
            value={formData.purchaseUrl || ''}
            onChange={e => handleFieldChange('purchaseUrl', e.target.value)}
            style={{
              width: '100%', padding: '11px 14px',
              border: `1px solid ${errors.purchaseUrl ? '#dc2626' : '#cbd5e1'}`,
              borderRadius: 6, fontSize: 15, outline: 'none',
              boxSizing: 'border-box', transition: 'border-color 0.2s',
            }}
            placeholder="https://..."
          />
          {errors.purchaseUrl && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {errors.purchaseUrl}
            </span>
          )}
        </label>

        {/* Descripcion */}
        <label style={{ display: 'block', marginBottom: 28 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Descripcion
          </span>
          <textarea
            value={formData.description || ''}
            onChange={e => handleFieldChange('description', e.target.value)}
            rows={4}
            style={{
              width: '100%', padding: '11px 14px',
              border: '1px solid #cbd5e1',
              borderRadius: 6, fontSize: 15, outline: 'none',
              boxSizing: 'border-box', resize: 'vertical',
              fontFamily: 'inherit',
            }}
            placeholder="Descripcion del suplemento..."
          />
        </label>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: 6,
              padding: '11px 24px', fontWeight: 600, fontSize: 15,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.6 : 1, color: '#374151',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#e5e7eb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            style={{
              background: isSubmitting ? '#81c9c4' : '#38b2ac',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '11px 24px', fontWeight: 600, fontSize: 15,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#319795'; }}
            onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#38b2ac'; }}
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Suplemento'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplementFormModal;
